import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, fileUrl, socket, serverUrl } from '@/lib/api';
import type { Message, PublicUser, Party } from '@/lib/types';
import { go, parseHash, useRoute } from '@/lib/hash';
import { chatRoute } from '@/lib/lastChat';
import { watchProfileClicks } from '@/lib/profileClicks';
import { watchScrollbars } from '@/lib/scrollbars';
import { onOpenPalette } from '@/lib/palette';
import { onOpenFinder, type FinderRequest } from '@/lib/finder';
import { onOpenThread } from '@/lib/thread';
import { watchGamepads } from '@/lib/gamepad';
import { patchPresence } from '@/lib/optimistic';
import { markRead, useNotificationFeed, type Notification } from '@/lib/notifications';
import { SCALES } from '@/lib/fonts';
import { watchHistory } from '@/lib/history';
import { useSession } from '@/stores/session';
import { loadCustomEmoji } from '@/lib/emoji';
import { setIceServers, useVoice } from '@/stores/voice';
import { bridge } from '@/lib/bridge';
import { play } from '@/lib/sound';
import { BUILTIN, comboOf, globalBinds, inTextField, isGlobalAction } from '@/lib/keybinds';
import { SideNav } from '@/components/SideNav';
import { AppTitleBar } from '@/components/AppTitleBar';
import { ServerNotice } from '@/components/ServerNotice';
import { PhoneBar } from '@/components/PhoneBar';
import { useCrewChat } from '@/stores/crewChat';
import { TabBar, useNarrow } from '@/components/TabBar';
import { hotkeyFailures, noteHotkeyFired, setHotkeyFailures, setHotkeyMode } from '@/lib/globalKeys';
import { NotificationsView } from '@/views/NotificationsView';
import { TitleBar } from '@/components/TitleBar';
import { Ambience } from '@/components/Ambience';
import { BootScreen } from '@/components/BootScreen';
import { VoiceDock } from '@/components/VoiceDock';
import { IncomingCallHost } from '@/components/IncomingCall';
import { Boundary } from '@/components/Boundary';

/**
 * What to call a screen that failed.
 *
 * "Chat could not be drawn" tells somebody which part of the app is broken
 * and, just as usefully, which parts are not. "Error" tells them nothing.
 */
const TITLES_FOR_ERRORS: Record<string, string> = {
  home: 'Home',
  parties: 'Parties',
  party: 'This party',
  friends: 'Friends',
  games: 'Games',
  chat: 'Chat',
  crews: 'Your crew',
  badges: 'Badges',
  leaderboard: 'The leaderboard',
  notifications: 'Notifications',
  settings: 'Settings',
  staff: 'The staff console',
  u: 'This profile'
};
import { Palette } from '@/components/Palette';
import { MessageFinder } from '@/components/MessageFinder';
import { ThreadPanel } from '@/components/ThreadPanel';
import { FirstRun, firstRunWanted } from '@/components/FirstRun';
import { useInvites } from '@/components/Invites';
import { AnnouncementBar } from '@/components/Announcements';
import { absolute } from '@/components/user';
import { closeAllLayers } from '@/lib/layers';
import { setPlaying } from '@/lib/playing';
import { UserCardHost } from '@/components/user';
import { EmojiInfoHost } from '@/components/EmojiInfo';
import { CrewHost } from '@/components/Crew';
import { CrewTagCardHost } from '@/components/CrewTag';
import { ToastHost } from '@/components/ui';
import { AuthView } from '@/views/AuthView';
import { PartiesView } from '@/views/PartiesView';
import { PartyView } from '@/views/PartyView';
import { FriendsView } from '@/views/FriendsView';
import { ChatView } from '@/views/ChatView';
import { ProfileView } from '@/views/ProfileView';
import { ProfilePanelHost } from '@/components/ProfilePanel';
import { StaffView } from '@/views/StaffView';
import { ServerConsolePage } from '@/views/staff/Console';
import { HomeView } from '@/views/HomeView';
import { GamesView } from '@/views/GamesView';
import { SettingsView } from '@/views/SettingsView';
import { BadgesView } from '@/views/BadgesView';
import { LeaderboardView } from '@/views/LeaderboardView';
import { CrewsView } from '@/views/CrewsView';
import { Placeholder } from '@/views/Placeholder';
import { JoinView } from '@/views/JoinView';
import { isFavorite, isMuted } from '@/lib/mutes';

/* What an announcement toast is called, by how serious it is. */
/* The toast title for each kind of announcement. Matches the labels the
   embed itself uses, so a notification and the thing it points at agree. */
const KIND_TITLE: Record<string, string> = {
  danger: 'Pugo — Important',
  warning: 'Pugo — Heads up',
  success: 'Pugo — Now live',
  update: 'Pugo — Update',
  event: 'Pugo — Event',
  info: 'Pugo'
};

export function App() {
  return (
    <ToastHost>
      <Shell />
      <UserCardHost />
      <EmojiInfoHost />
      <CrewHost />
      <CrewTagCardHost />
      <ProfilePanelHost />
    </ToastHost>
  );
}

function Shell() {
  /*
   * One subscription per field, deliberately.
   *
   * useSession() with no selector subscribes to the whole store - and this
   * is the root of the application, so every settings save, every presence
   * tick and every arriving emoji list re-rendered the entire tree. That is
   * what "clicking something updates the whole page" was.
   */
  const me = useSession((s) => s.me);
  const booting = useSession((s) => s.booting);
  const catalog = useSession((s) => s.catalog);
  const settings = useSession((s) => s.settings);
  const offline = useSession((s) => s.offline);
  const [palette, setPalette] = useState(false);
  const [finder, setFinder] = useState<FinderRequest | null>(null);
  const [welcome, setWelcome] = useState(false);
  const [thread, setThread] = useState<string | null>(null);
  // Which settings tab to open, or null for closed - so any surface can
  // send you to a specific screen of it.
  const route = useRoute();
  const queryClient = useQueryClient();
  const voice = useVoice();
  const invites = useInvites();
  const ping = useSession((st) => st.ping);
  // A phone gets a bottom bar instead of the rail and the two sidebars.
  const narrow = useNarrow();

  /* Keeps the splash up for a readable moment on a cold start. */
  const [held, setHeld] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setHeld(false), 900);
    return () => clearTimeout(t);
  }, []);

  /*
   * Settings and profiles are panels, not pages.
   *
   * Plenty of places still link to #/settings and #/u/<name> - the palette,
   * the profile card, the voice dock - so the routes are caught here: the
   * panel opens and the address goes back to the screen you were already on,
   * which is where closing the panel should leave you.
   *
   * It has to be a real hash change, not history.replaceState. replaceState
   * fires no hashchange, so the address bar said one thing while the router
   * still held "settings" - and settings has no screen, so what you actually
   * got underneath the panel was "Nothing here. That page does not exist."
   */
  const lastScreen = useRef('parties');
  useEffect(() => {
    // A profile is a page now, not a panel over whatever you were doing.
    if (route.head) {
      lastScreen.current = [route.head, ...route.parts.slice(1)].filter(Boolean).join('/');
    }
  }, [route.head, route.parts]);

  /*
   * Moving to another page takes everything floating with it.
   *
   * A profile card left open over Settings belongs to the page it was opened
   * from, and the stack knows what is open, so this is one call rather than a
   * list of things to remember to close.
   */
  useEffect(() => { closeAllLayers(); }, [route.head]);

  /* Records screens as you move, so the back button has a trail to walk. */
  useEffect(() => watchHistory(), []);

  /* Makes every avatar in the app open the profile it belongs to. */
  useEffect(() => watchProfileClicks(), []);
  useEffect(() => watchScrollbars(), []);

  /* Anywhere in the app can ask for the palette; the shell owns it. */
  useEffect(() => onOpenPalette(() => setPalette(true)), []);
  useEffect(() => onOpenFinder((req) => setFinder(req)), []);
  useEffect(() => onOpenThread((id) => setThread(id)), []);
  /* A controller drives the app only while the setting is on and a pad is
     plugged in; nothing polls otherwise. */
  const padOn = useSession((s) => !!s.settings?.bigTargets);
  useEffect(() => watchGamepads(() => padOn), [padOn]);
  /* Asked once, after the account exists and the catalogue has arrived. */
  useEffect(() => { if (firstRunWanted(me) && catalog) setWelcome(true); }, [me, catalog]);

  /*
   * Notifications arriving while the app is open.
   *
   * The feed keeps the cache current on its own; this only decides what you
   * hear. A sound per kind rather than one for everything, because the point
   * of a cue is to tell you whether it is worth looking - and nothing is
   * played for a notification about a conversation you are already reading,
   * which would be the app telling you about something on your screen.
   */
  const onNotification = useCallback((n: Notification) => {
    const here = location.hash.replace(/^#\/?/, '');
    if (n.route && here === n.route) return;
    if (n.kind === 'mention') play('mention');
    else if (n.kind === 'dm' || n.kind === 'groupMessage') play('message');
    else if (n.kind === 'partyInvite' || n.kind === 'friendRequest' || n.kind === 'crewRequest') play('invite');
    else if (n.kind === 'friendAccepted' || n.kind === 'crewAccepted' || n.kind === 'badge' || n.kind === 'levelUp') play('success');
  }, []);

  useNotificationFeed(onNotification);

  /*
   * One presence handler for the whole app.
   *
   * Screens used to invalidate their own lists when somebody's status
   * changed, which refetched a whole roster to move one dot - and the screens
   * that did not bother, like the conversation header, simply never updated.
   * This writes the change into every cached copy of that person instead: no
   * request, and nothing is left showing yesterday's status.
   */
  useEffect(() => socket.on('presence', (d: unknown) => {
    patchPresence(queryClient, d as { userId: string });
  }), [queryClient]);

  // The catalogue carries the ICE servers the voice mesh needs.
  useEffect(() => { setIceServers(catalog?.ice); }, [catalog]);

  /* The parties you are in, for where the Parties tab should land. */
  const myParties = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' }),
    enabled: !!me,
    // Short, because it decides where a nav button goes: a stale answer here
    // sends you somewhere you did not ask to be.
    staleTime: 3_000
  });

  /*
   * Parties are live everywhere, not only on the Parties screen: somebody
   * opening, filling or closing a party updates Home, Games and the list at
   * once, whatever page you happen to be on.
   */
  useEffect(() => {
    const refresh = () => void queryClient.invalidateQueries({ queryKey: ['parties'] });
    const off = [
      socket.on('party:created', refresh),
      socket.on('party:closed', refresh),
      socket.on('party:member_joined', refresh),
      socket.on('party:member_left', refresh),
      socket.on('party:updated', (payload: { party: Party }) => {
        queryClient.setQueriesData<{ parties: Party[]; total: number }>(
          { queryKey: ['parties'] },
          (old) => old && old.parties ? {
            ...old,
            parties: old.parties.map((p) => (p.id === payload.party.id ? { ...p, ...payload.party } : p))
          } : old
        );
      })
    ];
    return () => off.forEach((fn) => fn());
  }, [queryClient]);

  /* Both conversation lists, for the Chat badge. Cheap, and the socket keeps
     them current, so the badge moves the moment a message lands. */
  const dms = useQuery({
    queryKey: ['dms'],
    queryFn: () => api.get<{ threads: { unread: number }[] }>('/api/dms'),
    enabled: !!me,
    staleTime: 30_000
  });

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: endpoints.groups,
    enabled: !!me,
    staleTime: 30_000
  });

  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: endpoints.friends,
    enabled: !!me,
    staleTime: 30_000
  });

  /*
   * There used to be a second query on the ['dms'] key here, unused, whose
   * queryFn resolved to an empty thread list. Two observers share one cache
   * entry and one queryFn, so invalidating the key after a send could run
   * this one instead of the real request - and the sidebar came back with no
   * threads at all. Everyone you had messaged but not friended vanished,
   * because friends are merged in separately and survived it.
   */
  /*
   * The voice dock, in exactly one place.
   *
   * The desktop app used to show two at once - the floating one inside the
   * window and the detached one on top of everything - so the same controls
   * were on screen twice.
   *
   */
  // In the desktop app the dock is always the detached window, because that is
  // the one that can be moved anywhere on the screen - over a game, onto a
  // second monitor - and not just around inside Pugo. The in-app dock is what
  // the browser gets, where there is no second window to move into.
  const dockOut = !!bridge()?.dock;
  useEffect(() => {
    const api = bridge();
    if (!api?.dock) return;
    if (voice.partyId) void api.dock.open();
    else void api.dock.close();
  }, [voice.partyId]);

  /* What the dock shows. Pushed, because it holds no session of its own. */
  useEffect(() => {
    const api = bridge();
    if (!api?.dock || !voice.partyId || !me) return;
    void api.dock.push({
      name: me.displayName,
      avatar: me.avatarUrl ? absolute(me.avatarUrl) : null,
      role: me.role,
      roleLabel: me.role === 'admin' ? 'Admin' : me.role === 'mod' ? 'Moderator' : 'Member',
      muted: voice.muted,
      deafened: voice.deafened,
      sharing: voice.sharing,
      ping
    });
  }, [voice.partyId, voice.muted, voice.deafened, voice.sharing, me, ping]);

  /* And what its buttons do. The app owns the call; the dock only asks. */
  useEffect(() => {
    const api = bridge();
    if (!api?.dock?.onAction) return;
    return api.dock.onAction((action: string) => {
      if (action === 'mute') voice.toggleMute();
      else if (action === 'deafen') voice.toggleDeafen();
      else if (action === 'leave') void voice.leave();
      else if (action === 'settings') go('settings');
      else if (action === 'open' && voice.partyId) {
        /* A call can now be in a conversation, not only a party, so the dock
           has to send you to whichever kind of room you are actually in. */
        const id = voice.partyId;
        if (id.startsWith('dm:')) {
          const other = id.slice(3).split('|').find((x) => x !== me?.id);
          go(other ? 'chat' : 'chat');
        } else if (id.startsWith('g_')) go('chat/g/' + id);
        else go('party/' + id);
      }
      // A toggle: pressing share while sharing stops it. Swapping the source
      // reopened the picker the moment a share was closed.
      else if (action === 'share') {
        if (voice.sharing) voice.stopScreenShare();
        else void voice.startScreenShare(settings?.screenQuality ?? 'smooth');
      }
    });
  }, [voice, settings?.screenQuality]);

  /*
   * Telling the server what you are playing.
   *
   * The desktop app watches running processes and reports only a match
   * against the catalogue - see electron/games.js. Nothing is sent on the
   * web, where there is nothing to watch.
   */
  useEffect(() => {
    if (!me) return;
    return bridge()?.onGame?.((games) => {
      /* The shell sends a list. Older builds send one id or null, and both
         shapes are accepted so an app that has not updated yet still works. */
      const list = Array.isArray(games) ? games : games ? [games] : [];
      /* The app needs this too, not only the server: a button that starts a
         game should know when the game is already open. */
      setPlaying(list);
      socket.send('game:playing', { games: list, game: list[0] ?? null });
    });
  }, [me]);

  /*
   * Going away by itself.
   *
   * The setting existed, was saved, was shown in two places and did nothing
   * at all - there was no clock anywhere that read it. There is now.
   *
   * On the desktop the idle time comes from the operating system, because
   * the window can only see its own events: alt-tab into a game and the page
   * decides you have gone, which is exactly backwards. In a browser it falls
   * back to watching for input on the page, which is all a page can know.
   *
   * It only ever moves you between online and away, and only when you are on
   * one of those two: somebody who chose "do not disturb" or "invisible"
   * meant it, and a timer is not entitled to overrule them.
   */
  useEffect(() => {
    if (!me) return;
    const rules = settings?.statusRules?.idle;
    if (!rules?.auto) return;
    const after = Math.max(1, Math.min(120, rules.afterMinutes || 10)) * 60;

    let lastInput = Date.now();
    const touched = () => { lastInput = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];
    for (const name of events) window.addEventListener(name, touched, { passive: true });

    /* Set by us, so we know we are allowed to set it back. Somebody who goes
       to Online by hand while away has taken it back and we leave it alone. */
    let weSetIdle = false;

    const check = async () => {
      const desk = bridge();
      let idleFor = (Date.now() - lastInput) / 1000;
      if (desk?.idleSeconds) {
        try { idleFor = await desk.idleSeconds(); } catch { /* keep the page's guess */ }
      }
      const now = useSession.getState().me?.status;
      if (idleFor >= after && now === 'online') {
        weSetIdle = true;
        socket.send('idle', { away: true });
      } else if (idleFor < after && now === 'idle' && weSetIdle) {
        weSetIdle = false;
        socket.send('idle', { away: false });
      }
    };

    const timer = setInterval(() => void check(), 30_000);
    return () => {
      clearInterval(timer);
      for (const name of events) window.removeEventListener(name, touched);
    };
  }, [me, settings?.statusRules?.idle?.auto, settings?.statusRules?.idle?.afterMinutes]);

  /* Shell-level socket wiring: things that matter wherever you are. */
  useEffect(() => {
    if (!me) return;
    const off = [
      socket.on('party:closed', (d: { partyId: string }) => {
        if (voice.partyId === d.partyId) void voice.leave();
      }),
      /* Crew chat's unread counts, for the launcher and the sidebar. These are
         the server's counts: they only fall when the newest messages were
         actually on screen, never because a panel opened. */
      socket.on('unread', (d: { channelId: string; count: number }) => {
        useCrewChat.getState().setUnread(d.channelId, d.count);
      }),
      socket.on('ready', (d: { unread?: Record<string, number> }) => {
        useCrewChat.getState().seedUnread(d.unread ?? {});
      }),
      socket.on('party:kicked', (d: { partyId: string }) => {
        if (voice.partyId === d.partyId) void voice.leave();
        void queryClient.invalidateQueries({ queryKey: ['parties'] });
      }),
      socket.on('mention', (d: { body?: string; user?: PublicUser; channelId?: string; partyId?: string }) => {
        play('mention');
        notify({
          title: d.user?.displayName ?? 'Pugo',
          body: d.body ?? 'mentioned you',
          user: d.user,
          route: d.partyId ? 'party/' + d.partyId : d.user ? 'chat/' + d.user.username : undefined,
          kind: 'mention',
          urgent: true
        });
      }),
      socket.on('message:new', (msg: Message) => {
        // Your own message already made a noise when you pressed Enter.
        if (msg.userId === me.id) return;
        if (msg.scope === 'dm') void queryClient.invalidateQueries({ queryKey: ['dms'] });
        if (msg.scope === 'group') void queryClient.invalidateQueries({ queryKey: ['groups'] });

        // Discord's rule: no cue and no toast for the conversation already on
        // screen with the window in front. Anything else is worth a nudge.
        const looking = document.hasFocus() && onScreen(msg);
        if (looking) return;

        /* A conversation quieted until later says nothing at all - no sound,
           no pop-up. The unread count still rises, which is the point. */
        if (isMuted(msg.channelId)) return;

        /*
         * Do not disturb, doing something.
         *
         * The status used to be a coloured dot and nothing else. These are
         * the rules the person set for it in the status menu, and a direct
         * message can be exempted because plenty of people want exactly that.
         */
        const dnd = me.status === 'dnd';
        const rules = useSession.getState().settings?.statusRules;
        const spared = dnd && msg.scope === 'dm' && !!rules?.dnd.exceptDms;
        const quiet = dnd && !spared && rules?.dnd.sounds === false;
        const noToast = dnd && !spared && rules?.dnd.toasts === false;

        /*
         * Crew chat.
         *
         * "Mute Windows notifications for this crew" silences the desktop alert
         * and its sound - and only those. The unread count still rises (it is
         * the server's), the message is still delivered, and voice is a
         * different system entirely.
         */
        if (msg.scope === 'crew') {
          const crewId = msg.channelId.slice(3);
          if ((useSession.getState().settings?.mutedCrews ?? []).includes(crewId)) return;
          if (!quiet) play('message');
          if (noToast) return;
          notify({
            title: (msg.user?.displayName ?? 'Someone') + ' · Crew chat',
            body: msg.body || 'sent an attachment',
            user: msg.user,
            route: 'crews',
            kind: 'crew'
          });
          return;
        }

        if (!quiet) play('message');
        // A party message only breaks through if it named you; the mention
        // event covers that. DMs always do.
        if (msg.scope !== 'dm' || noToast) return;
        notify({
          title: msg.user?.displayName ?? 'New message',
          body: msg.body || 'sent an attachment',
          user: msg.user,
          route: msg.user ? 'chat/' + msg.user.username : undefined,
          kind: 'dm'
        });
      }),
      /*
       * News from the team.
       *
       * It arrives as a notification like anything else - an announcement
       * nobody is told about is one nobody reads - and the kind decides
       * the wording so a warning does not look like an update.
       */
      socket.on('announcement', (d: { announcement: { id: string; title: string; body: string; kind: string } }) => {
        const a = d.announcement;
        if (!a) return;
        void queryClient.invalidateQueries({ queryKey: ['announcements'] });
        play(a.kind === 'danger' ? 'mention' : 'message');
        notify({
          title: KIND_TITLE[a.kind] ?? 'Pugo',
          // Stripped of markup - a toast has no room to render it and
          // asterisks in a system notification look like a mistake.
          body: a.title + (a.body ? ' — ' + a.body.replace(/[*`]/g, '') : ''),
          route: 'chat/__pogu',
          kind: 'party',
          urgent: a.kind === 'danger'
        });
      }),

      /*
       * A moderator adding, removing or reserving an emoji changes it for
       * everyone.
       *
       * Fetched again rather than taken from the broadcast: which emoji you
       * may actually use depends on your own plan, and one payload sent to
       * every socket cannot carry a different answer for each of them.
       */
      socket.on('emoji:changed', () => {
        void loadCustomEmoji().then((emoji) => useSession.setState({ emoji }));
      }),

      // Same again when an administrator moves what a plan is worth: the
      // emoji you may use is one of the things it decides.
      socket.on('plans:changed', () => {
        void loadCustomEmoji().then((emoji) => useSession.setState({ emoji }));
        void queryClient.invalidateQueries({ queryKey: ['plans'] });
        void useSession.getState().refresh();
      }),
      /*
       * A sound was all these did.
       *
       * Nothing told the queries the answer had changed, so a friend request
       * or an invite chimed and then sat there invisible until something else
       * happened to refetch - which is why the badge never appeared when the
       * thing actually arrived.
       */
      /* A profile card and a profile page both say where you stand with
         somebody, and both were being left on whatever they said when they
         were opened - so a request cancelled in one place still read as sent
         in another. Anything that changes a friendship refreshes both. */
      socket.on('friend:request', () => {
        play('invite');
        void queryClient.invalidateQueries({ queryKey: ['friends'] });
        void queryClient.invalidateQueries({ queryKey: ['user'] });
      }),
      socket.on('friend:accepted', () => {
        void queryClient.invalidateQueries({ queryKey: ['friends'] });
        void queryClient.invalidateQueries({ queryKey: ['user'] });
      }),
      socket.on('friend:removed', () => {
        void queryClient.invalidateQueries({ queryKey: ['friends'] });
        void queryClient.invalidateQueries({ queryKey: ['user'] });
      }),
      socket.on('party:invite', () => {
        play('invite');
        void queryClient.invalidateQueries({ queryKey: ['invites'] });
      })
    ];
    return () => off.forEach((fn) => fn());
  }, [me, queryClient, voice]);


  /* Global shortcuts.

     Built-ins always listen. Everything else comes from what you have added
     in settings - nothing is bound until you bind it, so the app never eats a
     key your game wanted. */
  useEffect(() => {
    if (!me) return;
    const bound = new Map<string, string>();
    for (const [id, combo] of Object.entries(BUILTIN)) bound.set(combo, id);
    for (const [action, combo] of Object.entries(settings?.keybinds ?? {})) {
      if (combo) bound.set(combo, action);
    }

    /*
     * One place that knows what an action does.
     *
     * Two things ask for it: a key pressed while Pugo is focused, and the
     * main process relaying a system-wide shortcut. The second path existed
     * on the Electron side and was exposed by the preload, but nothing here
     * ever subscribed to it - so every global hotkey fired into nothing and
     * only worked while the app had focus.
     */
    function run(action: string) {
      switch (action) {
        case "palette": setPalette((v) => !v); break;
        case "settings": go("settings"); break;
        case "createParty": go("parties?create=1"); break;
        case "goHome": go("home"); break;
        case "goParties": go("parties"); break;
        case "goFriends": go("friends"); break;
        case "goChat": go("chat"); break;
        case "goGames": go("games"); break;
        case "goNotifications": go("notifications"); break;
        case "goCrew": go("crews"); break;
        case "goPlayers": go("players"); break;
        case "goBadges": go("badges"); break;
        case "focusComposer": {
          const box = document.querySelector<HTMLTextAreaElement>('textarea[aria-label]');
          box?.focus();
          break;
        }
        case "markAllRead": void markRead(queryClient); break;
        case "toggleCrewChat": {
          const cc = useCrewChat.getState();
          cc.setMode(cc.mode === 'open' || cc.mode === 'expanded' ? 'minimized' : 'open');
          if (cc.mode !== 'open' && cc.mode !== 'expanded') go("crews");
          break;
        }
        case "zoomIn":
        case "zoomOut":
        case "zoomReset": {
          const at = SCALES.findIndex((s) => s.id === (settings?.uiScale ?? 'm'));
          const next = action === 'zoomReset' ? 2 : Math.max(0, Math.min(SCALES.length - 1, (at < 0 ? 2 : at) + (action === 'zoomIn' ? 1 : -1)));
          void useSession.getState().saveSettings({ uiScale: SCALES[next]!.id as 'm' });
          break;
        }
        case "toggleTheme": {
          const light = ['warm', 'light', 'paper', 'sky', 'mint', 'lavender', 'peach'].includes(settings?.theme ?? 'graphite');
          void useSession.getState().saveSettings({ theme: light ? 'graphite' : 'warm' });
          break;
        }
        case "toggleStreamMode": void useSession.getState().saveSettings({ streamMode: !settings?.streamMode }); break;
        case "toggleMute": if (voice.partyId) voice.toggleMute(); break;
        case "toggleDeafen": if (voice.partyId) voice.toggleDeafen(); break;
        case "leaveVoice": if (voice.partyId) void voice.leave(); break;
        case "toggleShare":
          if (voice.partyId) {
            if (voice.sharing) voice.stopScreenShare();
            else void voice.startScreenShare(settings?.screenQuality ?? "balanced");
          }
          break;
        case "toggleSidebar": document.body.classList.toggle("hide-side"); break;
      }
    }

    // The other half: shortcuts the OS delivered while Pugo was in the
    // background. The main process has already decided whether to raise the
    // window; all that is left is doing the thing.
    const offShortcut = bridge()?.onShortcut?.((action: string) => {
      // Recorded before it is acted on, so the keybinds screen can show that
      // the system-wide path is alive even when the action itself is silent.
      noteHotkeyFired(action);
      run(action);
    });

    /*
     * Whether the operating system is already delivering this one.
     *
     * On the desktop the keybinds you added yourself are watched system-wide,
     * so pressing mute inside Pugo arrives twice: once through the hook, once
     * through this listener. Two toggles is no toggle, which is why mute
     * appeared to mute and unmute on a single press. The built-ins are not
     * registered system-wide, so those stay ours.
     */
    const mine = settings?.keybinds ?? {};
    const osHasIt = (action: string) =>
      !!bridge()?.setGlobalHotkeys
      && isGlobalAction(action)
      && !!mine[action]
      && !hotkeyFailures().includes(action);

    function onKey(e: KeyboardEvent) {
      // Holding a key repeats it. One press, one action.
      if (e.repeat) return;
      const combo = comboOf(e);
      if (!combo) return;
      const action = bound.get(combo);
      if (!action) return;
      if (osHasIt(action)) return;
      // A shortcut without a modifier must not fire while typing.
      if (inTextField(e.target) && !e.ctrlKey && !e.metaKey && !e.altKey) return;
      e.preventDefault();
      run(action);
    }

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      offShortcut?.();
    };
  }, [me, settings, voice]);

  /*
   * The same bindings, handed to the OS.
   *
   * A keydown listener only ever hears keys the app has focus for, which is
   * precisely when a mute shortcut is no use - you are in a game, or in a
   * browser. Electron can claim them system-wide, so the ones you bound
   * yourself work from wherever you are. Nothing is claimed until you bind
   * it, and combos without a modifier are refused outright rather than
   * taking a letter key away from every program on the machine.
   */
  useEffect(() => {
    const api = bridge();
    if (!api?.setGlobalHotkeys) return;
    void api.setGlobalHotkeys(globalBinds(settings?.keybinds ?? {}))
      // Which ones the OS would not give us, so the settings screen can say
      // so instead of leaving a dead key looking bound.
      .then((res) => {
        setHotkeyFailures(res?.failed ?? []);
        setHotkeyMode(res?.passthrough !== false);
      })
      .catch(() => setHotkeyFailures([]));
  }, [settings?.keybinds]);
  /*
   * Looking for a new version, without being asked.
   *
   * The updater only ever ran when somebody opened Settings and pressed the
   * button, so the update button in the top bar could not appear until you
   * had already gone looking - which is the one place you do not need to be
   * told. It checks shortly after launch and every half hour after that.
   *
   * Nothing downloads or restarts on its own; finding one only lights the
   * button up.
   */
  useEffect(() => {
    const api = bridge();
    if (!api?.update || !me) return;

    const look = () => { void api.update.check(serverUrl()).catch(() => {}); };

    // A moment after launch, so it is not competing with signing in.
    const first = window.setTimeout(look, 4000);
    const repeat = window.setInterval(look, 30 * 60 * 1000);

    // And whenever the machine comes back from sleep or reconnects, which is
    // when a release is most likely to have appeared since you last looked.
    const onBack = () => { if (document.visibilityState === 'visible') look(); };
    window.addEventListener('online', look);
    document.addEventListener('visibilitychange', onBack);

    return () => {
      window.clearTimeout(first);
      window.clearInterval(repeat);
      window.removeEventListener('online', look);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [me]);

  /* Clicking a notification lands you where it came from. */
  useEffect(() => {
    const api = bridge();
    if (api?.onRoute) return api.onRoute((route) => go(route));

    /* Browser build: permission is asked for through an explicit control
       (crew chat's notification menu), never on page load - a prompt nobody
       asked for is the one most people deny. */
    return;
  }, [me]);

  /* Keep the tray tooltip and taskbar badge in step with what is waiting. */
  useEffect(() => {
    const api = bridge();
    if (!api?.setBadge) return;
    void api.setBadge(friends.data?.incoming.length ?? 0);
  }, [friends.data]);

  /*
   * The loading screen holds for a beat.
   *
   * Boot is usually quicker than a frame, so the splash used to flash past -
   * long enough to see something happen, not long enough to read, which
   * reads as a glitch rather than as the app starting.
   */
  if (booting || held) return <BootScreen />;

  /*
   * No server, no app.
   *
   * Everything on every screen comes from the server, so with it unreachable
   * the alternative is a shell full of empty lists and a sign-in form that
   * cannot work. It waits here instead and keeps trying on its own.
   */
  if (offline) {
    return (
      <BootScreen
        offline
        note="Cannot reach Pugo right now. This will pick up on its own as soon as the connection is back."
      />
    );
  }

  if (!me) {
    return (
      <div className="flex h-full flex-col">
        <TitleBar />
        <AuthView />
      </div>
    );
  }

  /*
   * What the badges count.
   *
   * `unread` was hardcoded to zero, so the Chat tab never showed a badge no
   * matter how many messages were waiting. It is the sum of what the two
   * conversation lists already report per thread - no new endpoint needed,
   * and both lists are kept fresh by the socket below.
   */
  const unreadDms = (dms.data?.threads ?? []).reduce((n: number, t) => n + (t.unread || 0), 0);
  const unreadGroups = (groups.data?.groups ?? []).reduce((n: number, g) => n + (g.unread || 0), 0);

  const counts = {
    friends: friends.data?.incoming.length ?? 0,
    unread: unreadDms + unreadGroups,
    invites: invites.length
  };


  /*
   * Where the Parties tab goes.
   *
   * If you are in a party, that is what you mean by parties - the browse
   * list is where you go when you are not in one. Coming back to the app and
   * being dropped into a grid of strangers, with your own party three clicks
   * away, is the wrong default. The party screen has its own arrow back out
   * to the list.
   */
  const mine = myParties.data?.parties ?? [];
  /*
   * isMember as well as the scope.
   *
   * The list is cached, and leaving a party does not always refetch it
   * before you next tap the tab - so this used to walk you back into a party
   * you had already left. The flag comes from the server for you
   * specifically, so a stale row fails the test.
   */
  const currentParty = mine.find((p) => !p.closed && p.isMember) ?? null;
  const partiesRoute = currentParty ? 'party/' + currentParty.id : 'parties';

  /*
   * The Parties button asks the server, not the cache.
   *
   * Create a party, go to Chat, press Parties: the cached "mine" list could
   * still be from before the party existed, so the button went to the browse
   * list instead of your party. It now checks fresh - a local request that
   * takes a moment - and falls back to the cache only if that fails.
   */
  const goParties = () => {
    void queryClient.fetchQuery({
      queryKey: ['parties', 'mine'],
      queryFn: () => endpoints.parties({ scope: 'mine' }),
      staleTime: 0
    }).then((fresh) => {
      const cur = fresh.parties.find((p) => !p.closed && p.isMember);
      go(cur ? 'party/' + cur.id : 'parties');
    }, () => go(partiesRoute));
  };




  return (
    <Boundary what="Pugo" fatal>
    {/* Behind everything, and only when the theme is one of the scenes. */}
    <Ambience />
    <div className="flex h-full flex-col">
      {/* The desktop title bar: search, create, bell, staff tools and the
          window controls. See AppTitleBar for why it came back. */}
      {narrow ? null : (
        <AppTitleBar
          route={navRoute(route.head)}
          onNavigate={(id) => go(id)}
          onSearch={() => setPalette(true)}
          onCreate={() => go('parties?create=1')}
        />
      )}
      {/*
        A rail on the left, not a bar across the top.

        Every reference screen is built around a sidebar, and it also answers
        D05 from the earlier brief: the old header carried main pages, search,
        create, badges, crew, settings, staff, notifications and profile in one
        row, and at the reviewed desktop width some of the labels simply
        disappeared. A vertical rail has room for five destinations at a
        readable size, and each page owns its own header.

        Phones keep the bottom tab bar below - a 248px rail on a 390px screen
        is not navigation, it is the screen.
      */}
      <div className="flex min-h-0 flex-1">
        {narrow ? null : (
          <SideNav
            route={navRoute(route.head)}
            counts={counts}
            onNavigate={(id) => (id === 'parties' ? goParties() : go(id === 'chat' ? chatRoute() : id))}
            onOpenAccount={() => go('settings')}
            onSearch={() => setPalette(true)}
          />
        )}

        <main className="stage flex min-h-0 min-w-0 flex-1 flex-col">
          {narrow ? (
            <PhoneBar route={navRoute(route.head)} onSearch={() => setPalette(true)} />
          ) : null}
          {/* Whoever runs the server, saying why something is off. Above the
              screen rather than inside it, because it is about all of them. */}
          <ServerNotice />
          <AnnouncementBar />
          {/* Keyed on the route so the element remounts and the entry
              animation re-runs; without a key React reuses the node and the
              change happens with no transition at all. */}
          <div key={route.head} className="screen-in flex min-h-0 flex-1 flex-col">
            <Boundary key={route.head} what={TITLES_FOR_ERRORS[route.head] ?? 'This screen'}>
              <Screen head={route.head} parts={route.parts} />
            </Boundary>
          </div>
        </main>
      </div>

      {narrow ? (
        <TabBar
          route={navRoute(route.head)}
          counts={counts}
          onNavigate={(r) => (r === 'parties' ? goParties() : go(r === 'chat' ? chatRoute() : r))}
        />
      ) : null}

      <VoiceDock poppedOut={dockOut} />
      {/* Rings follow you across every screen, so this sits with the dock. */}
      <IncomingCallHost />
      {/* The first two minutes, for an account that has told us nothing. */}
      {welcome ? <FirstRun onClose={() => setWelcome(false)} /> : null}
      {thread ? <ThreadPanel messageId={thread} onClose={() => setThread(null)} /> : null}
      {palette ? <Palette onClose={() => setPalette(false)} /> : null}
      {finder ? (
        <MessageFinder
          start={finder.start}
          channelId={finder.channelId}
          channelName={finder.channelName}
          onClose={() => setFinder(null)}
        />
      ) : null}
    </div>
    </Boundary>
  );
}

/** A party page still belongs to Parties as far as the nav is concerned. */
function navRoute(head: string): string {
  if (head === 'party') return 'parties';
  if (head === 'leaderboard') return 'leaderboard';
  return head;
}

function Screen({ head, parts }: { head: string; parts: string[] }) {
  const me = useSession((s) => s.me);

  switch (head) {
    case 'home':
      return <HomeView />;
    case 'games':
      return <GamesView />;
    case 'settings':
      return <SettingsView />;
    case 'parties':
      return <PartiesView />;
    case 'party':
      return parts[1] ? <PartyView id={parts[1]} /> : <PartiesView />;
    /* An invite link somebody pasted somewhere. */
    case 'join':
      return parts[1] ? <JoinView token={parts[1]} /> : <PartiesView />;
    case 'friends':
      return <FriendsView />;
    case 'notifications':
      return <NotificationsView />;
    case 'chat':
      // chat/g/<id> is a group; chat/<name> is a person. One screen either
      // way - the list, the log and the composer are the same.
      return parts[1] === 'g'
        ? <ChatView groupId={parts[2]} />
        : <ChatView username={parts[1]} />;
    case 'u':
      return <ProfileView username={parts[1] ?? me?.username ?? ''} />;
    case 'crews':
      return <CrewsView />;
    case 'badges':
      return <BadgesView />;
    case 'leaderboard':
      return <LeaderboardView />;
    /*
     * Server health is its own page.
     *
     * It lived as a tab inside the staff console, which is where you go to
     * deal with people - reports, bans, permissions. Those are two different
     * jobs at two different moments: one is "somebody is being a problem",
     * the other is "is the server all right". Wanting the second and having
     * to walk through the first is how you end up not checking.
     *
     * Admins only. It reads the machine and it arms the guard, and both of
     * those are the owner's business rather than a moderator's.
     */
    case 'status':
      return me?.role === 'admin'
        ? <ServerConsolePage />
        : <Placeholder title="Not for you" note="Server status is for administrators." />;
    case 'staff':
      /*
       * Admin only, at the route as well as in the bar.
       * Hiding the tab is presentation; this is the part that means a
       * normal user typing the address in gets nothing.
       */
      return me?.role === 'admin'
        ? <StaffView />
        : <Placeholder title="Not for you" note="The staff console is for administrators." />;
    default:
      return <Placeholder title="Nothing here" note="That page does not exist." />;
  }
}

/**
 * Is this message in the conversation currently on screen? Used to decide
 * whether a message deserves a cue at all - the one you are reading does not.
 */
function onScreen(msg: Message): boolean {
  const { head, parts } = parseHash();
  if (msg.scope === 'party') return head === 'party' && parts[1] === msg.channelId;
  /* Crew chat is "on screen" only when its panel is showing and scrolled to
     the newest message - a minimised launcher or someone reading history
     should still hear about it. */
  if (msg.scope === 'crew') {
    const cc = useCrewChat.getState();
    return head === 'crews' && (cc.mode === 'open' || cc.mode === 'expanded') && cc.atBottom;
  }
  if (head !== 'chat' || !parts[1]) return false;
  // DM channels are dm_<idA>|<idB>; the route carries a username, so the
  // author is the only side worth comparing.
  return parts[1] === msg.user?.username;
}

/**
 * One desktop notification. Falls back to the Web Notification API in the
 * browser build, where there is no bridge to hand it to.
 */
/** Whether now falls inside quiet time, including a span that crosses midnight. */
export function inQuietTime(quiet: { on: boolean; from: string; to: string } | undefined): boolean {
  if (!quiet?.on) return false;
  const mins = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const d = new Date();
  const now = d.getHours() * 60 + d.getMinutes();
  const from = mins(quiet.from);
  const to = mins(quiet.to);
  return from <= to ? now >= from && now < to : now >= from || now < to;
}

function notify(opts: {
  title: string;
  body: string;
  user?: PublicUser;
  route?: string;
  urgent?: boolean;
  /** Which switch in Settings governs this one. */
  kind?: string;
}) {
  const settings = useSession.getState().settings;
  if (settings && !settings.notifications) return;
  // Each kind can be silenced on its own; unset means allowed.
  if (opts.kind && settings?.notify && settings.notify[opts.kind] === false) return;
  /*
   * Quiet time, and who is allowed through it.
   *
   * Somebody you pinned can be let past - the three people whose message at
   * midnight is worth the interruption - and only if that was switched on.
   */
  const spared = !!settings?.favoriteExceptions && !!opts.user && isFavorite(opts.user.id);
  if (inQuietTime(settings?.quiet) && !spared) return;

  // The line along the bottom of the toast, so you know what kind of thing
  // it is before reading it.
  const KIND_LABEL: Record<string, string> = {
    dm: 'Direct message',
    mention: 'Mentioned you',
    invite: 'Party invite',
    friend: 'Friend request',
    party: 'Party',
    call: 'Voice'
  };
  // Nothing to surface if the window is already in front and focused.
  if (document.hasFocus() && !document.hidden && !opts.urgent) return;

  const icon = opts.user?.avatarUrl ? fileUrl(opts.user.avatarUrl) : undefined;

  const api = bridge();
  if (api?.notify) {
    void api.notify(opts.title, opts.body, {
      icon,
      route: opts.route,
      urgent: opts.urgent,
      kindLabel: opts.kind ? KIND_LABEL[opts.kind] : undefined
    });
    return;
  }

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const n = new Notification(opts.title, { body: opts.body, icon, silent: true });
  n.onclick = () => {
    window.focus();
    if (opts.route) go(opts.route);
    n.close();
  };
}
